import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  Tooltip,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
// import zoomPlugin from "chartjs-plugin-zoom";
import {
  FaAngleLeft,
  FaAngleRight,
  FaChartArea,
  FaCalendar,
  FaCircle,
  FaNoteSticky,
} from "react-icons/fa6";
import TopMenu from "../top-menu/TopMenu";
import BottomMenu from "../bottom-menu/BottomMenu";
import { getProfile, getAllMoods, getDayStreak } from "../../api/mainApi";
import { useTelegram } from "../../context/TelegramContext";
import StatisticsBlocks from "./StatisticsBlocks";
import Calendar from "./Calendar";
import Notes from "./Notes";
import FullScreenPopup from "../fullScreenPopup/FullScreenPopup";
import ActivitySphere from "./ActivitySphere";


ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  Tooltip,
  Filler
);

const Statistics = () => {
  const tg = useTelegram();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [view, setView] = useState("calendar");
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [mode, setMode] = useState("week");
  const [chartData, setChartData] = useState({ labels: [], data: [] });
  const [loading, setLoading] = useState(false);
  const [regDate, setRegDate] = useState(new Date());
  const [dayStreak, setdayStreak] = useState(
    localStorage.getItem("dayStreak") || null
  );
  const [achievements, setAchievements] = useState(null);
  // Стан для збереження всіх настроїв
  const [allMoods, setAllMoods] = useState(null);
  const [errorPopup, setErrorPopup] = useState(null);

  useEffect(() => {
    // Завантаження даних один раз
    const fetchAllMoods = async () => {
      setLoading(true);
      try {
        const data = await getAllMoods();
        // Припускаємо, що data має структуру { reg_date, moods: [...] }
        setRegDate(new Date(data.reg_date));
        setAllMoods(data.moods); // зберігаємо всі настрої
        setErrorPopup(data.moods?.length === 0 || data.moods === null || data.moods === undefined);
      } catch (error) {
        console.error("Помилка завантаження даних:", error);
      } finally {
        setLoading(false);
      }
    };
    const fetchDayStreak = async () => {
      try {
        const data = await getDayStreak();
        await setdayStreak(data.day_streak);
        await localStorage.setItem("dayStreak", data.day_streak);
      } catch (error) {
        console.error("Помилка отримання Day Strike або настроїв:", error);
      }
    };
    const fetchAchievements = async () => {
      try {
        const data = await getProfile();
        setAchievements(data.achievements);
      } catch (error) {
        console.error("Error fetching achievements:", error);
      }
    };

    fetchDayStreak();
    fetchAchievements();
    fetchAllMoods();
  }, []); // Виконується лише один раз при монтуванні компонента

  useEffect(() => {
    // Перевірка, чи вже завантажено всі дані
    if (!allMoods) return;

    let filteredMoods = [];
    const now = new Date();

    if (mode === "week") {
      // Фільтруємо настрої за останні 7 днів
      const startDate = new Date();
      startDate.setDate(now.getDate() - 7);
      filteredMoods = allMoods.filter((mood) => {
        const moodDate = new Date(mood.date * 1000);
        return moodDate >= startDate && moodDate <= now;
      });
    } else {
      // Режим "місяць": фільтруємо за обраним місяцем і роком
      filteredMoods = allMoods.filter((mood) => {
        const moodDate = new Date(mood.date * 1000);
        return (
          moodDate.getMonth() === currentMonth &&
          moodDate.getFullYear() === currentYear
        );
      });
    }

    if (!filteredMoods.length) {
      setChartData({ labels: [], data: [] });
      return;
    }

    // Групуємо настрої за повною датою (YYYY-MM-DD) для коректного сортування
    const aggregatedData = filteredMoods.reduce((acc, mood) => {
      const moodDate = new Date(mood.date * 1000);
      const dateKey = moodDate.toISOString().split("T")[0]; // YYYY-MM-DD
      if (!acc[dateKey]) {
        acc[dateKey] = { sum: 0, count: 0 };
      }
      acc[dateKey].sum += mood.mood;
      acc[dateKey].count += 1;
      return acc;
    }, {});

    // Сортуємо ключі за повною датою
    const sortedDates = Object.keys(aggregatedData).sort(
      (a, b) => new Date(a) - new Date(b)
    );

    // Використовуємо лише номер дня (DD) для labels
    const newLabels = sortedDates.map((date) =>
      new Date(date).getDate().toString()
    );
    const newData = sortedDates.map(
      (date) => aggregatedData[date].sum / aggregatedData[date].count
    );

    setChartData({ labels: newLabels, data: newData });
  }, [allMoods, mode, currentMonth, currentYear]);

  const moodImages = ["/1.webp", "/2.webp", "/3.webp", "/4.webp", "/5.webp"];

  const year = currentYear;
  const canGoPrev =
    currentYear > regDate.getFullYear() ||
    (currentYear === regDate.getFullYear() &&
      currentMonth > regDate.getMonth());

  const canGoNext =
    currentYear < new Date().getFullYear() ||
    (currentYear === new Date().getFullYear() &&
      currentMonth < new Date().getMonth());

  const handleMonthChange = (direction) => {
    let newMonth = currentMonth + direction;
    let newYear = currentYear;

    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }

    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
  };

  const data = {
    labels: chartData.labels,
    datasets: [
      {
        label: "Mood Level:",
        data: chartData.data,
        borderColor: (ctx) => {
          const chart = ctx.chart;
          const { ctx: canvasCtx, chartArea } = chart;
          if (!chartArea) return;

          const gradient = canvasCtx.createLinearGradient(
            0,
            chartArea.bottom,
            0,
            chartArea.top
          );
          gradient.addColorStop(0, "#f43f5e");
          gradient.addColorStop(0.25, "#f97316");
          gradient.addColorStop(0.5, "#eab308");
          gradient.addColorStop(0.75, "#84cc16");
          gradient.addColorStop(1, "#10b981");
          return gradient;
        },
        borderWidth: 4,
        pointRadius: chartData.data.length === 1 ? 3 : 0, // Якщо одне значення — зробити точку більшою
        pointHoverRadius: chartData.data.length === 1 ? 5 : 5,
        tension: 0.4,
        spanGaps: true, // Дозволяє рендерити точку, навіть якщо вона одна
        fill: chartData.data.length > 1, // Заповнюємо фон тільки якщо є більше 1 точки
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 10, // Додай більше відступу зліва
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        ticks: {
          stepSize: 1,
          display: true,
          color: "#6b7280",
        },
        grid: {
          display: true,
          color: "#1f2937",
        },
      },
      y: {
        min: 0.8, // Трохи нижче мінімуму
        max: 5.2, // Трохи вище максимуму
        ticks: {
          stepSize: 1,
          callback: function (value) {
            return ""; // Завжди повертаємо пустий рядок
          },
        },
        grid: {
          color: function (context) {
            return Number.isInteger(context.tick.value)
              ? "#1f2937"
              : "transparent";
          },
        },
      },
    },
  };

  const imagePlugin = {
    id: "imagePlugin",
    afterDraw: (chart) => {
      const { ctx, scales } = chart;
      const yScale = scales.y;

      moodImages.forEach((src, index) => {
        const img = new Image();
        img.src = src;
        img.onload = () => {
          const yPos = yScale.getPixelForValue(index + 1);
          ctx.drawImage(img, 0, yPos - 10, 20, 20);
        };
      });
    },
  };

  ChartJS.register(imagePlugin);

  return (
    <div className="flex flex-col h-dvh">
      {(errorPopup && !loading) ? (
        <FullScreenPopup
          status="warning"
          text={t("data-undefined")}
          buttonText={t("markTheMood")}
          onClose={() => {
            setErrorPopup(null);
            navigate("/mood-menu");
          }}
        />
      ) : (
        <></>
      )}
      <TopMenu />
      <div className="flex-1 h-full overflow-y-auto pb-2 hidden-scrollbar lg:custom-scrollbar">
        {/* Кнопки перемикання */}
        <div className="grid grid-cols-3 gap-2 my-4 text-sm sm:text-base">
          <button
            className={`p-1 rounded-full ${
              view === "calendar" ? "bg-slate-950" : "bg-slate-800"
            }`}
            onClick={() => setView("calendar")}
          >
            <FaCalendar className="inline -mt-1 me-1" /> {t("calendar")}
          </button>
          <button
            className={`p-1 rounded-full ${
              view === "chart" ? "bg-slate-950" : "bg-slate-800"
            }`}
            disabled={loading}
            onClick={() => setView("chart")}
          >
            <FaChartArea className="inline -mt-1 me-1" /> {t("chart")}
          </button>
          <button
            className={`p-1 rounded-full ${
              view === "notes" ? "bg-slate-950" : "bg-slate-800"
            }`}
            disabled={loading}
            onClick={() => setView("notes")}
          >
            <FaNoteSticky className="inline -mt-1 me-1" /> {t("notes")}
          </button>
        </div>
        {/* Додаткові кнопки */}
        {view === "chart" && (
          <div className="grid grid-cols-2 items-center gap-x-2 mb-2">
            <button
              className={`py-1 text-sm rounded-full ${
                mode === "week" ? "bg-slate-950" : "bg-slate-800"
              }`}
              onClick={() => setMode("week")}
            >
              {t("periods.week")}
            </button>
            <button
              className={`py-1 text-sm rounded-full ${
                mode === "all-time" ? "bg-slate-950" : "bg-slate-800"
              }`}
              onClick={() => setMode("all-time")}
            >
              {t("periods.all")}
            </button>
          </div>
        )}
        {/* Відображення графіка */}
        {mode === "all-time" && view === "chart" && (
          <div className="flex justify-between">
            <h2 className="uppercase font-bold text-xl">
              {new Date(year, currentMonth).toLocaleString("default", {
                month: "long",
                year: "numeric",
              })}
            </h2>
            {regDate != undefined && (
              <div className="flex items-center gap-x-4">
                <button
                  onClick={() => canGoPrev && handleMonthChange(-1)}
                  className={`p-2 rounded-full ${
                    canGoPrev
                      ? "bg-slate-800"
                      : "bg-slate-500 opacity-50 cursor-not-allowed"
                  }`}
                  disabled={!canGoPrev}
                >
                  <FaAngleLeft />
                </button>
                <button
                  onClick={() => canGoNext && handleMonthChange(1)}
                  className={`p-2 rounded-full ${
                    canGoNext
                      ? "bg-slate-800"
                      : "bg-slate-500 opacity-50 cursor-not-allowed"
                  }`}
                  disabled={!canGoNext}
                >
                  <FaAngleRight />
                </button>
              </div>
            )}
          </div>
        )}
        <div className="w-full">
          {view === "chart" && (
            <div className="">
              <div className="h-42 relative">
                <Line data={data} options={options} />
                {!loading && !chartData.data.length && (
                  <div className="absolute flex items-center justify-center top-0 w-full h-full bg-slate-950/70">
                    <p>{t("noData")}</p>
                  </div>
                )}
              </div>
              {/* <div className="flex justify-around flex-wrap gap-x-1 text-sm text-gray-300">
                {[
                  { color: "emerald-500", label: "beautiful" },
                  { color: "lime-500", label: "good" },
                  { color: "yellow-500", label: "neutral" },
                  { color: "orange-500", label: "bad" },
                  { color: "rose-500", label: "terrible" },
                ].map(({ color, label }) => (
                  <div key={label}>
                    <p>
                      <FaCircle className={`text-${color} inline w-2 -mt-px`} /> {t(label)}
                    </p>
                  </div>
                ))}
              </div> */}
            </div>
          )}
          {view === "calendar" && (
            <Calendar
              tg={tg}
              allMoods={allMoods}
              regDate={regDate}
              loading={loading}
              setLoading={setLoading}
            />
          )}
          {view === "notes" && (
            <Notes
              allMoods={allMoods}
              regDate={regDate}
              loading={loading}
              setLoading={setLoading}
            />
          )}
        </div>
        <ActivitySphere 
        allMoods={allMoods}
        />
        <StatisticsBlocks
          allMoods={allMoods}
          dayStreak={dayStreak}
          achievements={achievements}
        />
      </div>
      <BottomMenu />
    </div>
  );
};

export default Statistics;
